import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { Checkbox } from '../../components/ui/Checkbox';
import Icon from '../../components/AppIcon';

const UserProfileSettingsCenter = () => {
  const { user, userProfile, company, isCompanyAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Profile form state
  const [profileForm, setProfileForm] = useState({
    full_name: '',
    email: '',
    department: '',
    role: '',
    phone: '',
    job_title: ''
  });

  // Security form state
  const [securityForm, setSecurityForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
    two_factor_enabled: false,
    session_timeout: '30'
  });

  // Notifications form state
  const [notificationsForm, setNotificationsForm] = useState({
    email_alerts: true,
    processing_notifications: true,
    report_delivery: true,
    security_alerts: true,
    frequency: 'immediate'
  });

  // Data preferences form state
  const [dataPreferencesForm, setDataPreferencesForm] = useState({
    default_file_format: 'csv',
    column_mapping_template: 'standard',
    confidence_threshold: '85',
    export_format: 'excel',
    auto_save_mappings: true,
    include_confidence_scores: true
  });

  // Team management state (for admins)
  const [teamMembers, setTeamMembers] = useState([]);
  const [inviteForm, setInviteForm] = useState({
    email: '',
    role: 'company_user',
    full_name: ''
  });

  // Audit log state
  const [auditLogs, setAuditLogs] = useState([]);

  // Initialize form data
  useEffect(() => {
    if (userProfile) {
      setProfileForm({
        full_name: userProfile?.full_name || '',
        email: userProfile?.email || '',
        department: userProfile?.department || '',
        role: userProfile?.role || '',
        phone: userProfile?.phone || '',
        job_title: userProfile?.job_title || ''
      });
    }

    // Load team members if admin
    if (isCompanyAdmin) {
      loadTeamMembers();
    }

    // Load audit logs
    loadAuditLogs();
  }, [userProfile, isCompanyAdmin]);

  const loadTeamMembers = async () => {
    // Mock team members data
    setTeamMembers([
      {
        id: '1',
        full_name: 'John Doe',
        email: 'john.doe@company.com',
        role: 'company_user',
        is_active: true,
        last_login: '2024-10-16T10:30:00Z',
        created_at: '2024-09-15T09:00:00Z'
      },
      {
        id: '2', 
        full_name: 'Jane Smith',
        email: 'jane.smith@company.com',
        role: 'company_user',
        is_active: true,
        last_login: '2024-10-15T14:20:00Z',
        created_at: '2024-09-20T11:15:00Z'
      }
    ]);
  };

  const loadAuditLogs = async () => {
    // Mock audit logs
    setAuditLogs([
      {
        id: '1',
        action: 'Profile Updated',
        timestamp: '2024-10-16T10:30:00Z',
        details: 'Updated contact information'
      },
      {
        id: '2',
        action: 'Password Changed',
        timestamp: '2024-10-15T09:15:00Z',
        details: 'Password successfully changed'
      },
      {
        id: '3',
        action: 'Login',
        timestamp: '2024-10-16T08:00:00Z',
        details: 'Successful login from 192.168.1.100'
      }
    ]);
  };

  const handleProfileSubmit = async (e) => {
    e?.preventDefault();
    setIsLoading(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      setSuccessMessage('Profile updated successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Profile update failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSecuritySubmit = async (e) => {
    e?.preventDefault();
    setIsLoading(true);
    
    try {
      // Validate passwords match
      if (securityForm?.new_password !== securityForm?.confirm_password) {
        throw new Error('Passwords do not match');
      }
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      setSuccessMessage('Security settings updated successfully!');
      setSecurityForm(prev => ({
        ...prev,
        current_password: '',
        new_password: '',
        confirm_password: ''
      }));
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Security update failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNotificationsSubmit = async (e) => {
    e?.preventDefault();
    setIsLoading(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      setSuccessMessage('Notification preferences updated successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Notifications update failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDataPreferencesSubmit = async (e) => {
    e?.preventDefault();
    setIsLoading(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      setSuccessMessage('Data preferences updated successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Data preferences update failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInviteUser = async (e) => {
    e?.preventDefault();
    setIsLoading(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      setSuccessMessage('User invitation sent successfully!');
      setInviteForm({ email: '', role: 'company_user', full_name: '' });
      loadTeamMembers(); // Reload team members
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('User invitation failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleUserStatus = async (userId, isActive) => {
    setIsLoading(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 500));
      setTeamMembers(prev => 
        prev?.map(member => 
          member?.id === userId 
            ? { ...member, is_active: !isActive }
            : member
        )
      );
      setSuccessMessage(`User ${!isActive ? 'activated' : 'deactivated'} successfully!`);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('User status toggle failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetToDefaults = (section) => {
    switch (section) {
      case 'notifications':
        setNotificationsForm({
          email_alerts: true,
          processing_notifications: true,
          report_delivery: true,
          security_alerts: true,
          frequency: 'immediate'
        });
        break;
      case 'data':
        setDataPreferencesForm({
          default_file_format: 'csv',
          column_mapping_template: 'standard',
          confidence_threshold: '85',
          export_format: 'excel',
          auto_save_mappings: true,
          include_confidence_scores: true
        });
        break;
      default:
        break;
    }
  };

  const handleExportUserData = () => {
    // Simulate data export
    const userData = {
      profile: profileForm,
      preferences: { ...notificationsForm, ...dataPreferencesForm },
      audit_logs: auditLogs
    };
    
    const dataStr = JSON.stringify(userData, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `user_data_${userProfile?.email}_${new Date()?.toISOString()?.split('T')?.[0]}.json`;
    
    const linkElement = document.createElement('a');
    linkElement?.setAttribute('href', dataUri);
    linkElement?.setAttribute('download', exportFileDefaultName);
    linkElement?.click();
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: 'User' },
    { id: 'security', label: 'Account Security', icon: 'Shield' },
    { id: 'notifications', label: 'Notifications', icon: 'Bell' },
    { id: 'data', label: 'Data Preferences', icon: 'Settings' },
    ...(isCompanyAdmin ? [{ id: 'team', label: 'Team Management', icon: 'Users' }] : []),
    { id: 'audit', label: 'Audit Trail', icon: 'FileText' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-foreground">User Profile & Settings Center</h1>
                <p className="text-muted-foreground mt-2">
                  Manage your account, preferences, and team settings
                </p>
              </div>
              
              <div className="flex items-center space-x-3">
                <Button
                  variant="outline"
                  onClick={handleExportUserData}
                  iconName="Download"
                  iconPosition="left"
                  size="sm"
                >
                  Export User Data
                </Button>
              </div>
            </div>
          </div>

          {/* Success Message */}
          {successMessage && (
            <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center">
                <Icon name="CheckCircle" className="text-green-500 mr-2" size={20} />
                <p className="text-green-800">{successMessage}</p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Sidebar Navigation */}
            <div className="lg:col-span-1">
              <nav className="space-y-1">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      activeTab === tab?.id
                        ? 'bg-blue-100 text-blue-700 border-l-4 border-blue-700' :'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    <Icon name={tab?.icon} size={16} className="mr-3" />
                    {tab?.label}
                  </button>
                ))}
              </nav>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-3">
              <div className="bg-card border border-border rounded-lg p-6">
                {/* Profile Tab */}
                {activeTab === 'profile' && (
                  <div>
                    <h2 className="text-xl font-semibold text-foreground mb-6">Profile Information</h2>
                    
                    <form onSubmit={handleProfileSubmit} className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Input
                          label="Full Name"
                          type="text"
                          value={profileForm?.full_name}
                          onChange={(e) => setProfileForm(prev => ({ ...prev, full_name: e?.target?.value }))}
                          required
                        />
                        
                        <Input
                          label="Email"
                          type="email"
                          value={profileForm?.email}
                          onChange={(e) => setProfileForm(prev => ({ ...prev, email: e?.target?.value }))}
                          required
                        />
                        
                        <Input
                          label="Department"
                          type="text"
                          value={profileForm?.department}
                          onChange={(e) => setProfileForm(prev => ({ ...prev, department: e?.target?.value }))}
                        />
                        
                        <Input
                          label="Job Title"
                          type="text"
                          value={profileForm?.job_title}
                          onChange={(e) => setProfileForm(prev => ({ ...prev, job_title: e?.target?.value }))}
                        />
                        
                        <Input
                          label="Phone Number"
                          type="tel"
                          value={profileForm?.phone}
                          onChange={(e) => setProfileForm(prev => ({ ...prev, phone: e?.target?.value }))}
                        />
                        
                        <Select
                          label="Role"
                          value={profileForm?.role}
                          onChange={(value) => setProfileForm(prev => ({ ...prev, role: value }))}
                          options={[
                            { value: 'company_admin', label: 'Company Admin' },
                            { value: 'company_user', label: 'Company User' }
                          ]}
                          disabled={!isCompanyAdmin}
                        />
                      </div>
                      
                      <div className="flex justify-end">
                        <Button
                          type="submit"
                          variant="default"
                          loading={isLoading}
                          iconName="Save"
                          iconPosition="left"
                        >
                          Save Changes
                        </Button>
                      </div>
                    </form>
                  </div>
                )}

                {/* Security Tab */}
                {activeTab === 'security' && (
                  <div>
                    <h2 className="text-xl font-semibold text-foreground mb-6">Account Security</h2>
                    
                    <form onSubmit={handleSecuritySubmit} className="space-y-6">
                      <div className="space-y-4">
                        <Input
                          label="Current Password"
                          type="password"
                          value={securityForm?.current_password}
                          onChange={(e) => setSecurityForm(prev => ({ ...prev, current_password: e?.target?.value }))}
                        />
                        
                        <Input
                          label="New Password"
                          type="password"
                          value={securityForm?.new_password}
                          onChange={(e) => setSecurityForm(prev => ({ ...prev, new_password: e?.target?.value }))}
                        />
                        
                        <Input
                          label="Confirm New Password"
                          type="password"
                          value={securityForm?.confirm_password}
                          onChange={(e) => setSecurityForm(prev => ({ ...prev, confirm_password: e?.target?.value }))}
                        />
                      </div>

                      <div className="border-t border-border pt-6">
                        <h3 className="text-lg font-medium text-foreground mb-4">Security Options</h3>
                        
                        <div className="space-y-4">
                          <Checkbox
                            label="Enable Two-Factor Authentication"
                            checked={securityForm?.two_factor_enabled}
                            onChange={(checked) => setSecurityForm(prev => ({ ...prev, two_factor_enabled: checked }))}
                          />
                          
                          <Select
                            label="Session Timeout (minutes)"
                            value={securityForm?.session_timeout}
                            onChange={(value) => setSecurityForm(prev => ({ ...prev, session_timeout: value }))}
                            options={[
                              { value: '15', label: '15 minutes' },
                              { value: '30', label: '30 minutes' },
                              { value: '60', label: '1 hour' },
                              { value: '120', label: '2 hours' }
                            ]}
                          />
                        </div>
                      </div>
                      
                      <div className="flex justify-end">
                        <Button
                          type="submit"
                          variant="default"
                          loading={isLoading}
                          iconName="Shield"
                          iconPosition="left"
                        >
                          Update Security Settings
                        </Button>
                      </div>
                    </form>
                  </div>
                )}

                {/* Notifications Tab */}
                {activeTab === 'notifications' && (
                  <div>
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-xl font-semibold text-foreground">Notification Preferences</h2>
                      <Button
                        variant="outline"
                        onClick={() => handleResetToDefaults('notifications')}
                        size="sm"
                      >
                        Reset to Defaults
                      </Button>
                    </div>
                    
                    <form onSubmit={handleNotificationsSubmit} className="space-y-6">
                      <div className="space-y-4">
                        <h3 className="text-lg font-medium text-foreground">Email Notifications</h3>
                        
                        <div className="space-y-3">
                          <Checkbox
                            label="Email Alerts for System Updates"
                            checked={notificationsForm?.email_alerts}
                            onChange={(checked) => setNotificationsForm(prev => ({ ...prev, email_alerts: checked }))}
                          />
                          
                          <Checkbox
                            label="Processing Completion Notifications"
                            checked={notificationsForm?.processing_notifications}
                            onChange={(checked) => setNotificationsForm(prev => ({ ...prev, processing_notifications: checked }))}
                          />
                          
                          <Checkbox
                            label="Report Delivery Notifications"
                            checked={notificationsForm?.report_delivery}
                            onChange={(checked) => setNotificationsForm(prev => ({ ...prev, report_delivery: checked }))}
                          />
                          
                          <Checkbox
                            label="Security Alert Notifications"
                            checked={notificationsForm?.security_alerts}
                            onChange={(checked) => setNotificationsForm(prev => ({ ...prev, security_alerts: checked }))}
                          />
                        </div>
                      </div>

                      <div className="border-t border-border pt-6">
                        <Select
                          label="Notification Frequency"
                          value={notificationsForm?.frequency}
                          onChange={(value) => setNotificationsForm(prev => ({ ...prev, frequency: value }))}
                          options={[
                            { value: 'immediate', label: 'Immediate' },
                            { value: 'hourly', label: 'Hourly Digest' },
                            { value: 'daily', label: 'Daily Digest' },
                            { value: 'weekly', label: 'Weekly Digest' }
                          ]}
                        />
                      </div>
                      
                      <div className="flex justify-end">
                        <Button
                          type="submit"
                          variant="default"
                          loading={isLoading}
                          iconName="Bell"
                          iconPosition="left"
                        >
                          Save Notification Settings
                        </Button>
                      </div>
                    </form>
                  </div>
                )}

                {/* Data Preferences Tab */}
                {activeTab === 'data' && (
                  <div>
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-xl font-semibold text-foreground">Data Preferences</h2>
                      <Button
                        variant="outline"
                        onClick={() => handleResetToDefaults('data')}
                        size="sm"
                      >
                        Reset to Defaults
                      </Button>
                    </div>
                    
                    <form onSubmit={handleDataPreferencesSubmit} className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Select
                          label="Default File Format"
                          value={dataPreferencesForm?.default_file_format}
                          onChange={(value) => setDataPreferencesForm(prev => ({ ...prev, default_file_format: value }))}
                          options={[
                            { value: 'csv', label: 'CSV' },
                            { value: 'xlsx', label: 'Excel' },
                            { value: 'json', label: 'JSON' }
                          ]}
                        />
                        
                        <Select
                          label="Column Mapping Template"
                          value={dataPreferencesForm?.column_mapping_template}
                          onChange={(value) => setDataPreferencesForm(prev => ({ ...prev, column_mapping_template: value }))}
                          options={[
                            { value: 'standard', label: 'Standard Template' },
                            { value: 'custom', label: 'Custom Template' },
                            { value: 'auto', label: 'Auto-detect' }
                          ]}
                        />
                        
                        <Select
                          label="Confidence Threshold (%)"
                          value={dataPreferencesForm?.confidence_threshold}
                          onChange={(value) => setDataPreferencesForm(prev => ({ ...prev, confidence_threshold: value }))}
                          options={[
                            { value: '70', label: '70%' },
                            { value: '80', label: '80%' },
                            { value: '85', label: '85%' },
                            { value: '90', label: '90%' },
                            { value: '95', label: '95%' }
                          ]}
                        />
                        
                        <Select
                          label="Export Format"
                          value={dataPreferencesForm?.export_format}
                          onChange={(value) => setDataPreferencesForm(prev => ({ ...prev, export_format: value }))}
                          options={[
                            { value: 'excel', label: 'Excel' },
                            { value: 'csv', label: 'CSV' },
                            { value: 'pdf', label: 'PDF' }
                          ]}
                        />
                      </div>

                      <div className="border-t border-border pt-6">
                        <h3 className="text-lg font-medium text-foreground mb-4">Workflow Options</h3>
                        
                        <div className="space-y-3">
                          <Checkbox
                            label="Auto-save Column Mappings"
                            checked={dataPreferencesForm?.auto_save_mappings}
                            onChange={(checked) => setDataPreferencesForm(prev => ({ ...prev, auto_save_mappings: checked }))}
                          />
                          
                          <Checkbox
                            label="Include Confidence Scores in Exports"
                            checked={dataPreferencesForm?.include_confidence_scores}
                            onChange={(checked) => setDataPreferencesForm(prev => ({ ...prev, include_confidence_scores: checked }))}
                          />
                        </div>
                      </div>
                      
                      <div className="flex justify-end">
                        <Button
                          type="submit"
                          variant="default"
                          loading={isLoading}
                          iconName="Settings"
                          iconPosition="left"
                        >
                          Save Data Preferences
                        </Button>
                      </div>
                    </form>
                  </div>
                )}

                {/* Team Management Tab (Admin Only) */}
                {activeTab === 'team' && isCompanyAdmin && (
                  <div>
                    <h2 className="text-xl font-semibold text-foreground mb-6">Team Management</h2>
                    
                    {/* Invite New User */}
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
                      <h3 className="text-lg font-medium text-foreground mb-4">Invite New Team Member</h3>
                      
                      <form onSubmit={handleInviteUser} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <Input
                            label="Full Name"
                            type="text"
                            value={inviteForm?.full_name}
                            onChange={(e) => setInviteForm(prev => ({ ...prev, full_name: e?.target?.value }))}
                            required
                          />
                          
                          <Input
                            label="Email Address"
                            type="email"
                            value={inviteForm?.email}
                            onChange={(e) => setInviteForm(prev => ({ ...prev, email: e?.target?.value }))}
                            required
                          />
                          
                          <Select
                            label="Role"
                            value={inviteForm?.role}
                            onChange={(value) => setInviteForm(prev => ({ ...prev, role: value }))}
                            options={[
                              { value: 'company_user', label: 'Company User' },
                              { value: 'company_admin', label: 'Company Admin' }
                            ]}
                          />
                        </div>
                        
                        <div className="flex justify-end">
                          <Button
                            type="submit"
                            variant="default"
                            loading={isLoading}
                            iconName="UserPlus"
                            iconPosition="left"
                            size="sm"
                          >
                            Send Invitation
                          </Button>
                        </div>
                      </form>
                    </div>

                    {/* Team Members List */}
                    <div>
                      <h3 className="text-lg font-medium text-foreground mb-4">Current Team Members</h3>
                      
                      <div className="overflow-x-auto">
                        <table className="w-full border border-border rounded-lg">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Name</th>
                              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Email</th>
                              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Role</th>
                              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Status</th>
                              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Last Login</th>
                              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-border">
                            {teamMembers?.map((member) => (
                              <tr key={member?.id} className="hover:bg-gray-50">
                                <td className="px-4 py-3 text-sm text-foreground">{member?.full_name}</td>
                                <td className="px-4 py-3 text-sm text-foreground">{member?.email}</td>
                                <td className="px-4 py-3 text-sm">
                                  <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                                    member?.role === 'company_admin' ?'bg-blue-100 text-blue-800' :'bg-gray-100 text-gray-800'
                                  }`}>
                                    {member?.role === 'company_admin' ? 'Admin' : 'User'}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-sm">
                                  <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                                    member?.is_active 
                                      ? 'bg-green-100 text-green-800' :'bg-red-100 text-red-800'
                                  }`}>
                                    {member?.is_active ? 'Active' : 'Inactive'}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-sm text-muted-foreground">
                                  {new Date(member?.last_login)?.toLocaleDateString()}
                                </td>
                                <td className="px-4 py-3 text-sm">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleToggleUserStatus(member?.id, member?.is_active)}
                                    iconName={member?.is_active ? 'UserX' : 'UserCheck'}
                                    iconPosition="left"
                                  >
                                    {member?.is_active ? 'Deactivate' : 'Activate'}
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}

                {/* Audit Trail Tab */}
                {activeTab === 'audit' && (
                  <div>
                    <h2 className="text-xl font-semibold text-foreground mb-6">Audit Trail</h2>
                    
                    <div className="space-y-4">
                      {auditLogs?.map((log) => (
                        <div key={log?.id} className="border border-border rounded-lg p-4">
                          <div className="flex items-center justify-between mb-2">
                            <h3 className="text-sm font-medium text-foreground">{log?.action}</h3>
                            <span className="text-xs text-muted-foreground">
                              {new Date(log?.timestamp)?.toLocaleString()}
                            </span>
                          </div>
                          <p className="text-sm text-muted-foreground">{log?.details}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default UserProfileSettingsCenter;